import Plotly from "react-plotly.js"
import {Skeleton} from "primereact/skeleton"

import MetricCard from "../MetricCard.jsx"
import {fyLabel, useMetricsQuery} from "./util.jsx"

const CadenceChart = ({title, subtitle, weeks, values, orientees, target, color, isLoading}) => {
    const traces = [
        {
            x: weeks,
            y: values,
            customdata: orientees,
            name: "Average per orientee",
            type: "scatter",
            mode: "lines+markers",
            line: {color, shape: "spline", smoothing: 1.3},
            hovertemplate: "%{y:.2f} across %{customdata} orientees<extra></extra>",
        },
        {
            x: weeks,
            y: weeks.map(() => target),
            name: "Target",
            type: "scatter",
            mode: "lines",
            line: {color: "rgba(255,159,64,1)", dash: "dash"},
            hoverinfo: "skip",
        },
    ]

    const layout = {
        font: {
            family: "Inter, sans-serif"
        },
        xaxis: {tickangle: 45},
        yaxis: {rangemode: "tozero", tickformat: ".2f"},
        margin: {l: 50, r: 10, t: 20, b: 60},
        legend: {orientation: "h", x: 0.5, y: 1.15, xanchor: "center", yanchor: "bottom"},
        hovermode: "x unified",
    }

    const config = {displayModeBar: false, responsive: true}

    return (
        <div className="card card-xl-stretch mb-xl-8">
            <div className="card-header border-0 pt-5">
                <h3 className="card-title align-items-start flex-column">
                    <span className="card-label fw-bold fs-2 text-gray-900">{title}</span>
                    <span className="fs-6 fw-semibold text-gray-700">{subtitle}</span>
                </h3>
            </div>
            <div className="card-body pt-5 h-500px">
                {isLoading ? (
                    <Skeleton className="w-100 h-400px" />
                ) : weeks.length === 0 ? (
                    <div className="text-muted text-center py-10">No orientees match the current filters.</div>
                ) : (
                    <Plotly data={traces} layout={layout} config={config} style={{width: "100%"}} />
                )}
            </div>
        </div>
    )
}

const MetricsCompliance = ({filters = {}}) => {
    // const {data, isLoading, error} = useMetricsQuery("compliance", filters, {
    //     weeks: [],
    //     osr: {average: null, target: 1},
    //     nhsr: {average: null, target: 1},
    // })
    const error = null
    const isLoading = false
    const data2027 = {
        "weeks": [
            {
                "week": 1,
                "orientees": 256,
                "osr_submitted": 129,
                "nhsr_submitted": 75,
                "osr_rate": 0.5,
                "nhsr_rate": 0.29
            },
            {
                "week": 2,
                "orientees": 251,
                "osr_submitted": 142,
                "nhsr_submitted": 81,
                "osr_rate": 0.57,
                "nhsr_rate": 0.32
            },
            {
                "week": 3,
                "orientees": 245,
                "osr_submitted": 139,
                "nhsr_submitted": 66,
                "osr_rate": 0.57,
                "nhsr_rate": 0.27
            },
            {
                "week": 4,
                "orientees": 236,
                "osr_submitted": 143,
                "nhsr_submitted": 60,
                "osr_rate": 0.61,
                "nhsr_rate": 0.25
            },
            {
                "week": 5,
                "orientees": 225,
                "osr_submitted": 124,
                "nhsr_submitted": 67,
                "osr_rate": 0.55,
                "nhsr_rate": 0.3
            },
            {
                "week": 6,
                "orientees": 209,
                "osr_submitted": 114,
                "nhsr_submitted": 54,
                "osr_rate": 0.55,
                "nhsr_rate": 0.26
            },
            {
                "week": 7,
                "orientees": 197,
                "osr_submitted": 98,
                "nhsr_submitted": 43,
                "osr_rate": 0.5,
                "nhsr_rate": 0.22
            },
            {
                "week": 8,
                "orientees": 175,
                "osr_submitted": 94,
                "nhsr_submitted": 44,
                "osr_rate": 0.54,
                "nhsr_rate": 0.25
            },
            {
                "week": 9,
                "orientees": 148,
                "osr_submitted": 68,
                "nhsr_submitted": 32,
                "osr_rate": 0.46,
                "nhsr_rate": 0.22
            },
            {
                "week": 10,
                "orientees": 124,
                "osr_submitted": 57,
                "nhsr_submitted": 29,
                "osr_rate": 0.46,
                "nhsr_rate": 0.23
            },
            {
                "week": 11,
                "orientees": 85,
                "osr_submitted": 31,
                "nhsr_submitted": 19,
                "osr_rate": 0.36,
                "nhsr_rate": 0.22
            },
            {
                "week": 12,
                "orientees": 55,
                "osr_submitted": 17,
                "nhsr_submitted": 11,
                "osr_rate": 0.31,
                "nhsr_rate": 0.2
            },
            {
                "week": 13,
                "orientees": 35,
                "osr_submitted": 10,
                "nhsr_submitted": 4,
                "osr_rate": 0.29,
                "nhsr_rate": 0.11
            },
            {
                "week": 14,
                "orientees": 23,
                "osr_submitted": 3,
                "nhsr_submitted": 6,
                "osr_rate": 0.13,
                "nhsr_rate": 0.26
            },
            {
                "week": 15,
                "orientees": 18,
                "osr_submitted": 2,
                "nhsr_submitted": 0,
                "osr_rate": 0.11,
                "nhsr_rate": 0.0
            },
            {
                "week": 16,
                "orientees": 17,
                "osr_submitted": 2,
                "nhsr_submitted": 2,
                "osr_rate": 0.12,
                "nhsr_rate": 0.12
            },
            {
                "week": 17,
                "orientees": 16,
                "osr_submitted": 0,
                "nhsr_submitted": 0,
                "osr_rate": 0.0,
                "nhsr_rate": 0.0
            },
            {
                "week": 18,
                "orientees": 15,
                "osr_submitted": 3,
                "nhsr_submitted": 2,
                "osr_rate": 0.2,
                "nhsr_rate": 0.13
            },
            {
                "week": 19,
                "orientees": 14,
                "osr_submitted": 0,
                "nhsr_submitted": 0,
                "osr_rate": 0.0,
                "nhsr_rate": 0.0
            },
            {
                "week": 20,
                "orientees": 13,
                "osr_submitted": 0,
                "nhsr_submitted": 0,
                "osr_rate": 0.0,
                "nhsr_rate": 0.0
            }
        ],
        "osr": {
            "average": 0.5,
            "target": 1.0
        },
        "nhsr": {
            "average": 0.25,
            "target": 0.5
        }
    }

    const data2026 = {
        "weeks": [
            {
                "week": 1,
                "orientees": 812,
                "osr_submitted": 382,
                "nhsr_submitted": 162,
                "osr_rate": 0.47,
                "nhsr_rate": 0.2
            },
            {
                "week": 2,
                "orientees": 793,
                "osr_submitted": 423,
                "nhsr_submitted": 209,
                "osr_rate": 0.53,
                "nhsr_rate": 0.26
            },
            {
                "week": 3,
                "orientees": 783,
                "osr_submitted": 415,
                "nhsr_submitted": 212,
                "osr_rate": 0.53,
                "nhsr_rate": 0.27
            },
            {
                "week": 4,
                "orientees": 754,
                "osr_submitted": 399,
                "nhsr_submitted": 182,
                "osr_rate": 0.53,
                "nhsr_rate": 0.24
            },
            {
                "week": 5,
                "orientees": 708,
                "osr_submitted": 331,
                "nhsr_submitted": 179,
                "osr_rate": 0.47,
                "nhsr_rate": 0.25
            },
            {
                "week": 6,
                "orientees": 660,
                "osr_submitted": 327,
                "nhsr_submitted": 136,
                "osr_rate": 0.5,
                "nhsr_rate": 0.21
            },
            {
                "week": 7,
                "orientees": 625,
                "osr_submitted": 292,
                "nhsr_submitted": 122,
                "osr_rate": 0.47,
                "nhsr_rate": 0.2
            },
            {
                "week": 8,
                "orientees": 570,
                "osr_submitted": 270,
                "nhsr_submitted": 101,
                "osr_rate": 0.47,
                "nhsr_rate": 0.18
            },
            {
                "week": 9,
                "orientees": 484,
                "osr_submitted": 216,
                "nhsr_submitted": 82,
                "osr_rate": 0.45,
                "nhsr_rate": 0.17
            },
            {
                "week": 10,
                "orientees": 410,
                "osr_submitted": 170,
                "nhsr_submitted": 73,
                "osr_rate": 0.41,
                "nhsr_rate": 0.18
            },
            {
                "week": 11,
                "orientees": 321,
                "osr_submitted": 137,
                "nhsr_submitted": 56,
                "osr_rate": 0.43,
                "nhsr_rate": 0.17
            },
            {
                "week": 12,
                "orientees": 233,
                "osr_submitted": 83,
                "nhsr_submitted": 39,
                "osr_rate": 0.36,
                "nhsr_rate": 0.17
            },
            {
                "week": 13,
                "orientees": 167,
                "osr_submitted": 53,
                "nhsr_submitted": 32,
                "osr_rate": 0.32,
                "nhsr_rate": 0.19
            },
            {
                "week": 14,
                "orientees": 137,
                "osr_submitted": 37,
                "nhsr_submitted": 29,
                "osr_rate": 0.27,
                "nhsr_rate": 0.21
            },
            {
                "week": 15,
                "orientees": 103,
                "osr_submitted": 27,
                "nhsr_submitted": 15,
                "osr_rate": 0.26,
                "nhsr_rate": 0.15
            },
            {
                "week": 16,
                "orientees": 71,
                "osr_submitted": 19,
                "nhsr_submitted": 13,
                "osr_rate": 0.27,
                "nhsr_rate": 0.18
            },
            {
                "week": 17,
                "orientees": 57,
                "osr_submitted": 11,
                "nhsr_submitted": 16,
                "osr_rate": 0.19,
                "nhsr_rate": 0.28
            },
            {
                "week": 18,
                "orientees": 51,
                "osr_submitted": 12,
                "nhsr_submitted": 11,
                "osr_rate": 0.24,
                "nhsr_rate": 0.22
            },
            {
                "week": 19,
                "orientees": 48,
                "osr_submitted": 11,
                "nhsr_submitted": 11,
                "osr_rate": 0.23,
                "nhsr_rate": 0.23
            },
            {
                "week": 20,
                "orientees": 43,
                "osr_submitted": 8,
                "nhsr_submitted": 6,
                "osr_rate": 0.19,
                "nhsr_rate": 0.14
            }
        ],
        "osr": {
            "average": 0.46,
            "target": 1.0
        },
        "nhsr": {
            "average": 0.22,
            "target": 0.5
        }
    }

    const data2025 = {
        "weeks": [
            {
                "week": 1,
                "orientees": 558,
                "osr_submitted": 311,
                "nhsr_submitted": 91,
                "osr_rate": 0.56,
                "nhsr_rate": 0.16
            },
            {
                "week": 2,
                "orientees": 554,
                "osr_submitted": 301,
                "nhsr_submitted": 138,
                "osr_rate": 0.54,
                "nhsr_rate": 0.25
            },
            {
                "week": 3,
                "orientees": 541,
                "osr_submitted": 322,
                "nhsr_submitted": 133,
                "osr_rate": 0.6,
                "nhsr_rate": 0.25
            },
            {
                "week": 4,
                "orientees": 528,
                "osr_submitted": 275,
                "nhsr_submitted": 149,
                "osr_rate": 0.52,
                "nhsr_rate": 0.28
            },
            {
                "week": 5,
                "orientees": 497,
                "osr_submitted": 283,
                "nhsr_submitted": 127,
                "osr_rate": 0.57,
                "nhsr_rate": 0.26
            },
            {
                "week": 6,
                "orientees": 462,
                "osr_submitted": 247,
                "nhsr_submitted": 95,
                "osr_rate": 0.53,
                "nhsr_rate": 0.21
            },
            {
                "week": 7,
                "orientees": 415,
                "osr_submitted": 227,
                "nhsr_submitted": 96,
                "osr_rate": 0.55,
                "nhsr_rate": 0.23
            },
            {
                "week": 8,
                "orientees": 352,
                "osr_submitted": 195,
                "nhsr_submitted": 76,
                "osr_rate": 0.55,
                "nhsr_rate": 0.22
            },
            {
                "week": 9,
                "orientees": 297,
                "osr_submitted": 150,
                "nhsr_submitted": 62,
                "osr_rate": 0.51,
                "nhsr_rate": 0.21
            },
            {
                "week": 10,
                "orientees": 250,
                "osr_submitted": 135,
                "nhsr_submitted": 50,
                "osr_rate": 0.54,
                "nhsr_rate": 0.2
            },
            {
                "week": 11,
                "orientees": 210,
                "osr_submitted": 99,
                "nhsr_submitted": 33,
                "osr_rate": 0.47,
                "nhsr_rate": 0.16
            },
            {
                "week": 12,
                "orientees": 147,
                "osr_submitted": 60,
                "nhsr_submitted": 25,
                "osr_rate": 0.41,
                "nhsr_rate": 0.17
            },
            {
                "week": 13,
                "orientees": 105,
                "osr_submitted": 43,
                "nhsr_submitted": 24,
                "osr_rate": 0.41,
                "nhsr_rate": 0.23
            },
            {
                "week": 14,
                "orientees": 88,
                "osr_submitted": 34,
                "nhsr_submitted": 17,
                "osr_rate": 0.39,
                "nhsr_rate": 0.19
            },
            {
                "week": 15,
                "orientees": 72,
                "osr_submitted": 31,
                "nhsr_submitted": 16,
                "osr_rate": 0.43,
                "nhsr_rate": 0.22
            },
            {
                "week": 16,
                "orientees": 55,
                "osr_submitted": 22,
                "nhsr_submitted": 9,
                "osr_rate": 0.4,
                "nhsr_rate": 0.16
            },
            {
                "week": 17,
                "orientees": 31,
                "osr_submitted": 13,
                "nhsr_submitted": 10,
                "osr_rate": 0.42,
                "nhsr_rate": 0.32
            },
            {
                "week": 18,
                "orientees": 23,
                "osr_submitted": 6,
                "nhsr_submitted": 6,
                "osr_rate": 0.26,
                "nhsr_rate": 0.26
            },
            {
                "week": 19,
                "orientees": 10,
                "osr_submitted": 3,
                "nhsr_submitted": 2,
                "osr_rate": 0.3,
                "nhsr_rate": 0.2
            },
            {
                "week": 20,
                "orientees": 8,
                "osr_submitted": 2,
                "nhsr_submitted": 2,
                "osr_rate": 0.25,
                "nhsr_rate": 0.25
            }
        ],
        "osr": {
            "average": 0.53,
            "target": 1.0
        },
        "nhsr": {
            "average": 0.22,
            "target": 0.5
        }
    }

    const data2024 = {
        "weeks": [
            {
                "week": 1,
                "orientees": 207,
                "osr_submitted": 53,
                "nhsr_submitted": 0,
                "osr_rate": 0.26,
                "nhsr_rate": 0.0
            },
            {
                "week": 2,
                "orientees": 204,
                "osr_submitted": 64,
                "nhsr_submitted": 1,
                "osr_rate": 0.31,
                "nhsr_rate": 0.0
            },
            {
                "week": 3,
                "orientees": 202,
                "osr_submitted": 66,
                "nhsr_submitted": 0,
                "osr_rate": 0.33,
                "nhsr_rate": 0.0
            },
            {
                "week": 4,
                "orientees": 201,
                "osr_submitted": 63,
                "nhsr_submitted": 0,
                "osr_rate": 0.31,
                "nhsr_rate": 0.0
            },
            {
                "week": 5,
                "orientees": 192,
                "osr_submitted": 56,
                "nhsr_submitted": 2,
                "osr_rate": 0.29,
                "nhsr_rate": 0.01
            },
            {
                "week": 6,
                "orientees": 182,
                "osr_submitted": 57,
                "nhsr_submitted": 1,
                "osr_rate": 0.31,
                "nhsr_rate": 0.01
            },
            {
                "week": 7,
                "orientees": 174,
                "osr_submitted": 54,
                "nhsr_submitted": 0,
                "osr_rate": 0.31,
                "nhsr_rate": 0.0
            },
            {
                "week": 8,
                "orientees": 158,
                "osr_submitted": 42,
                "nhsr_submitted": 1,
                "osr_rate": 0.27,
                "nhsr_rate": 0.01
            },
            {
                "week": 9,
                "orientees": 140,
                "osr_submitted": 32,
                "nhsr_submitted": 0,
                "osr_rate": 0.23,
                "nhsr_rate": 0.0
            },
            {
                "week": 10,
                "orientees": 121,
                "osr_submitted": 23,
                "nhsr_submitted": 1,
                "osr_rate": 0.19,
                "nhsr_rate": 0.01
            },
            {
                "week": 11,
                "orientees": 97,
                "osr_submitted": 13,
                "nhsr_submitted": 0,
                "osr_rate": 0.13,
                "nhsr_rate": 0.0
            },
            {
                "week": 12,
                "orientees": 62,
                "osr_submitted": 12,
                "nhsr_submitted": 2,
                "osr_rate": 0.19,
                "nhsr_rate": 0.03
            },
            {
                "week": 13,
                "orientees": 41,
                "osr_submitted": 8,
                "nhsr_submitted": 0,
                "osr_rate": 0.2,
                "nhsr_rate": 0.0
            },
            {
                "week": 14,
                "orientees": 34,
                "osr_submitted": 2,
                "nhsr_submitted": 0,
                "osr_rate": 0.06,
                "nhsr_rate": 0.0
            },
            {
                "week": 15,
                "orientees": 21,
                "osr_submitted": 2,
                "nhsr_submitted": 0,
                "osr_rate": 0.1,
                "nhsr_rate": 0.0
            },
            {
                "week": 16,
                "orientees": 9,
                "osr_submitted": 1,
                "nhsr_submitted": 0,
                "osr_rate": 0.11,
                "nhsr_rate": 0.0
            },
            {
                "week": 17,
                "orientees": 3,
                "osr_submitted": 0,
                "nhsr_submitted": 0,
                "osr_rate": 0.0,
                "nhsr_rate": 0.0
            },
            {
                "week": 18,
                "orientees": 1,
                "osr_submitted": 0,
                "nhsr_submitted": 0,
                "osr_rate": 0.0,
                "nhsr_rate": 0.0
            },
            {
                "week": 19,
                "orientees": 0,
                "osr_submitted": 0,
                "nhsr_submitted": 0,
                "osr_rate": null,
                "nhsr_rate": null
            },
            {
                "week": 20,
                "orientees": 0,
                "osr_submitted": 0,
                "nhsr_submitted": 0,
                "osr_rate": null,
                "nhsr_rate": null
            }
        ],
        "osr": {
            "average": 0.27,
            "target": 1.0
        },
        "nhsr": {
            "average": 0.0,
            "target": 0.5
        }
    }

    const data2023 = {
        "weeks": [
            {
                "week": 1,
                "orientees": 177,
                "osr_submitted": 0,
                "nhsr_submitted": 0,
                "osr_rate": 0.0,
                "nhsr_rate": 0.0
            },
            {
                "week": 2,
                "orientees": 175,
                "osr_submitted": 0,
                "nhsr_submitted": 0,
                "osr_rate": 0.0,
                "nhsr_rate": 0.0
            },
            {
                "week": 3,
                "orientees": 175,
                "osr_submitted": 0,
                "nhsr_submitted": 0,
                "osr_rate": 0.0,
                "nhsr_rate": 0.0
            },
            {
                "week": 4,
                "orientees": 172,
                "osr_submitted": 0,
                "nhsr_submitted": 0,
                "osr_rate": 0.0,
                "nhsr_rate": 0.0
            },
            {
                "week": 5,
                "orientees": 163,
                "osr_submitted": 0,
                "nhsr_submitted": 0,
                "osr_rate": 0.0,
                "nhsr_rate": 0.0
            },
            {
                "week": 6,
                "orientees": 153,
                "osr_submitted": 0,
                "nhsr_submitted": 0,
                "osr_rate": 0.0,
                "nhsr_rate": 0.0
            },
            {
                "week": 7,
                "orientees": 148,
                "osr_submitted": 0,
                "nhsr_submitted": 0,
                "osr_rate": 0.0,
                "nhsr_rate": 0.0
            },
            {
                "week": 8,
                "orientees": 139,
                "osr_submitted": 0,
                "nhsr_submitted": 0,
                "osr_rate": 0.0,
                "nhsr_rate": 0.0
            },
            {
                "week": 9,
                "orientees": 122,
                "osr_submitted": 0,
                "nhsr_submitted": 0,
                "osr_rate": 0.0,
                "nhsr_rate": 0.0
            },
            {
                "week": 10,
                "orientees": 111,
                "osr_submitted": 0,
                "nhsr_submitted": 0,
                "osr_rate": 0.0,
                "nhsr_rate": 0.0
            },
            {
                "week": 11,
                "orientees": 89,
                "osr_submitted": 0,
                "nhsr_submitted": 0,
                "osr_rate": 0.0,
                "nhsr_rate": 0.0
            },
            {
                "week": 12,
                "orientees": 52,
                "osr_submitted": 0,
                "nhsr_submitted": 0,
                "osr_rate": 0.0,
                "nhsr_rate": 0.0
            },
            {
                "week": 13,
                "orientees": 36,
                "osr_submitted": 0,
                "nhsr_submitted": 0,
                "osr_rate": 0.0,
                "nhsr_rate": 0.0
            },
            {
                "week": 14,
                "orientees": 32,
                "osr_submitted": 0,
                "nhsr_submitted": 0,
                "osr_rate": 0.0,
                "nhsr_rate": 0.0
            },
            {
                "week": 15,
                "orientees": 28,
                "osr_submitted": 0,
                "nhsr_submitted": 0,
                "osr_rate": 0.0,
                "nhsr_rate": 0.0
            },
            {
                "week": 16,
                "orientees": 6,
                "osr_submitted": 0,
                "nhsr_submitted": 0,
                "osr_rate": 0.0,
                "nhsr_rate": 0.0
            },
            {
                "week": 17,
                "orientees": 2,
                "osr_submitted": 0,
                "nhsr_submitted": 0,
                "osr_rate": 0.0,
                "nhsr_rate": 0.0
            },
            {
                "week": 18,
                "orientees": 1,
                "osr_submitted": 0,
                "nhsr_submitted": 0,
                "osr_rate": 0.0,
                "nhsr_rate": 0.0
            },
            {
                "week": 19,
                "orientees": 0,
                "osr_submitted": 0,
                "nhsr_submitted": 0,
                "osr_rate": null,
                "nhsr_rate": null
            },
            {
                "week": 20,
                "orientees": 0,
                "osr_submitted": 0,
                "nhsr_submitted": 0,
                "osr_rate": null,
                "nhsr_rate": null
            }
        ],
        "osr": {
            "average": 0.0,
            "target": 1.0
        },
        "nhsr": {
            "average": 0.0,
            "target": 0.5
        }
    }


    let data = {};
    if (filters.fiscal_year === 2027) {
        data = data2027;
    } else if (filters.fiscal_year === 2026) {
        data = data2026;
    } else if (filters.fiscal_year === 2025) {
        data = data2025;
    } else if (filters.fiscal_year === 2024) {
        data = data2024;
    } else if (filters.fiscal_year === 2023) {
        data = data2023;
    }


    if (error) return <div className="alert alert-danger">Unable to load compliance.</div>

    // Weeks with no orientees are dropped rather than plotted as gaps: a
    // partial fiscal year would otherwise show a line that stops mid-chart.
    const populated = data.weeks.filter((w) => w.orientees > 0)
    const weekLabels = populated.map((w) => `Week ${w.week}`)
    const orientees = populated.map((w) => w.orientees)
    const fyDisplay = fyLabel(filters.fiscal_year)

    return (
        <>
            <h2 className="fw-bold mb-8">Report Cadence</h2>
            <div className="d-flex flex-column flex-lg-row gap-8">
                <MetricCard
                    title="Avg OSRs / Orientee / Week"
                    subtitle={fyDisplay}
                    value={data.osr.average?.toFixed(2) ?? "—"}
                    comparison={`Target: ${data.osr.target.toFixed(2)}`}
                    isLoading={isLoading}
                />
                <MetricCard
                    title="Avg NHSRs / Orientee / Week"
                    subtitle={fyDisplay}
                    value={data.nhsr.average?.toFixed(2) ?? "—"}
                    comparison={`Target: ${data.nhsr.target.toFixed(2)}`}
                    isLoading={isLoading}
                />
            </div>

            <div className="row g-5 g-xl-8">
                <div className="col-xl-6">
                    <CadenceChart
                        title="OSRs per Orientee by Week"
                        subtitle="Pooled across the fiscal year cohort, against target"
                        weeks={weekLabels}
                        values={populated.map((w) => w.osr_rate)}
                        orientees={orientees}
                        target={data.osr.target}
                        color="rgba(75,192,192,1)"
                        isLoading={isLoading}
                    />
                </div>
                <div className="col-xl-6">
                    <CadenceChart
                        title="NHSRs per Orientee by Week"
                        subtitle="Pooled across the fiscal year cohort, against target"
                        weeks={weekLabels}
                        values={populated.map((w) => w.nhsr_rate)}
                        orientees={orientees}
                        target={data.nhsr.target}
                        color="rgba(153,102,255,1)"
                        isLoading={isLoading}
                    />
                </div>
            </div>
        </>
    )
}

export default MetricsCompliance
